import { Box, Button, Stack, Typography } from "@mui/material";
import CircledBackButton from "@/features/Common/components/CircledBackButton";
import IconLoader from "@/features/Common/components/IconLoader";
import { useLockBodyScroll } from "@/hooks/useLockBodyScroll";
import { defaultFullscreenPageStyling, maxWidthConfig } from "@/theme";

export interface ScreenWrapperProps {
  onBack: () => void;
  title: string;
  description?: string;
  onInfoClick?: () => void;
  disableHorizontalPadding?: boolean;
  children: React.ReactNode;
}

export default function ScreenWrapper({
  onBack,
  title,
  description,
  onInfoClick,
  disableHorizontalPadding = false,
  children,
}: ScreenWrapperProps) {
  useLockBodyScroll();

  return (
    <Stack
      p={2}
      sx={{
        ...defaultFullscreenPageStyling,
        display: "flex",
        flexDirection: "column",
        maxWidth: maxWidthConfig.defaultMaxWidth,
        width: "100%",
        mx: "auto",
        ...(disableHorizontalPadding && { px: 0 }),
      }}
    >
      <Box
        component="header"
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 1,
        }}
      >
        <CircledBackButton onClick={onBack} showLabel={true} />
      </Box>

      <Stack width="100%" mt={2}>
        <Stack
          flexDirection="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            fontWeight={600}
            variant="h3"
            sx={{
              fontWeight: 700,
              fontSize: 26,
              lineHeight: 1.25,
            }}
          >
            {title}
          </Typography>
          {onInfoClick != null && (
            <Button
              variant="text"
              onClick={onInfoClick}
              aria-label="More information"
              style={{
                padding: 0,
                margin: 0,
                minWidth: "auto",
              }}
            >
              <IconLoader icon="InfoIcon" color="primary" />
            </Button>
          )}
        </Stack>
        {description != null && description !== "" && (
          <Stack mt={1}>
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 500,
                lineHeight: 1.5,
                color: "neutral.700",
              }}
            >
              {description}
            </Typography>
          </Stack>
        )}
      </Stack>

      <Box
        sx={{
          mt: 3,
          paddingBottom: (theme) =>
            `calc(${theme.spacing(5)} + ${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
        }}
      >
        {children}
      </Box>
    </Stack>
  );
}
